import { FC, useState } from 'react';

import Box from '@material-ui/core/Box';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Button from 'components/button';
import { IImage } from './ImageSorter';
import { GEMINI_IMPROVE_IMAGE_PROMPT_2 } from 'components/constants-ts';

export enum AI_MODEL {
  gemini = 'gemini',
  nanoBanana21 = 'gemini-nano-banana-2.1',
  chatgpt = 'chatgpt',
  sunburst = 'gpt-image-2.5-sunburst',
  flare = 'gpt-image-2.5-flare',
}

export const aiModelOptions = [
  { value: AI_MODEL.gemini, label: 'Gemini' },
  { value: AI_MODEL.nanoBanana21, label: 'Nano Banana 2.1 (4K)' },
  { value: AI_MODEL.chatgpt, label: 'GPT-Image 2' },
  { value: AI_MODEL.sunburst, label: 'GPT-Image 2.5 Sunburst (bäst kvalitet)' },
  { value: AI_MODEL.flare, label: 'GPT-Image 2.5 Flare (snabbast)' },
];

interface AiModelDropdownProps {
  model: AI_MODEL;
  setModel: React.Dispatch<React.SetStateAction<AI_MODEL>>;
}

export const AiModelDropdown: FC<AiModelDropdownProps> = ({ model, setModel }) => {
  return (
    <FormControl variant='outlined'>
      <InputLabel id="ai-model-label">AI-modell</InputLabel>
      <Select
        id="ai-model"
        labelId="ai-model-label"
        label="AI-modell"
        style={{ width: 360 }}
        value={model}
        onChange={(event) => setModel(event.target.value as AI_MODEL)}
      >
        {aiModelOptions.map(item => {
          return (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}

const imageRatioOptions = [
  { value: 'auto', label: 'Auto (utgå från originalbild)' },
  { value: '1:1', label: '1:1 (Kvadrat)' },
  { value: '4:3', label: '4:3 (Liggande)' },
  { value: '3:4', label: '3:4 (Stående)' },
  { value: '16:9', label: '16:9 (Bred)' },
  { value: '9:16', label: '9:16 (Smal)' },
];

interface GenerateEnvAIModalProps {
  imageData: IImage;
  onClose: () => void;
  onSubmit: (param: { prompt: string, model: AI_MODEL, ratio?: string }) => void;
}

export const GenerateEnvAIModal: FC<GenerateEnvAIModalProps> = ({ imageData, onSubmit, onClose }) => {
  const [prompt, setPrompt] = useState(GEMINI_IMPROVE_IMAGE_PROMPT_2);
  const [ratio, setRatio] = useState('auto');
  const [model, setModel] = useState(AI_MODEL.gemini);

  const handleSubmit = () => {
    onClose();
    onSubmit({ prompt, model, ratio: ratio === 'auto' ? '' : ratio });
  };

  return (
    <Dialog
      open
      PaperProps={{ style: { maxWidth: 900, width: '100%', maxHeight: 700, height: '100%' } }}
      onAbort={onClose}
    >
      <DialogTitle>Generera miljöbild</DialogTitle>

      <DialogContent>
        <Box
          display="flex"
          gridGap={24}
          flexDirection={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'center', md: 'start' }}
          style={{ height: "calc(100% - 80px)" }}
        >
          <Box flexShrink={0} display="flex" alignItems="start" justifyContent="center" width={{ xs: '100%', md: 150 }}>
            <img src={imageData.imgSrc} alt="" style={{ maxWidth: '100%', maxHeight: '100%' }} />
          </Box>

          <TextField
            fullWidth
            multiline
            label="Prompt"
            variant="outlined"
            style={{ height: '100%' }}
            value={prompt}
            onChange={event => setPrompt(event.target.value)}
            InputProps={{ style: { height: '100%' } }}
            inputProps={{ style: { height: '100%', overflow: 'auto' } }}
          />
        </Box>
        <Box display="flex" mt={2} justifyContent="end" gridGap={16}>
          <AiModelDropdown model={model} setModel={setModel} />
          <FormControl variant='outlined'>
            <InputLabel id="image-format-label">Bildformat</InputLabel>
            <Select
              id="image-format"
              labelId="image-format-label"
              label="Bildformat"
              style={{ width: 250 }}
              value={ratio}
              onChange={(event) => setRatio(event.target.value as string)}
            >
              {imageRatioOptions.map(item => {
                return (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions>
        <Box display="flex" gridGap={16} px={2} py={1}>
          <Button size="small" color="primary" onClick={onClose}>
            Avbryt
          </Button>
          <Button size="small" variant="contained" color="primary" onClick={handleSubmit}>
            Förbättra
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateEnvAIModal;
